import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Loader2, ShieldX } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import type { ReactNode } from "react";

export function AdminGuard({ children }: { children: ReactNode }) {
  const { loading, profileLoading, isPlatformAdmin, user, signOut } = useAuth();
  const [unauthorized, setUnauthorized] = useState(false);
  const signOutCalledRef = useRef(false);

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!user) return;
    if (signOutCalledRef.current) return;

    if (!isPlatformAdmin) {
      signOutCalledRef.current = true;
      setUnauthorized(true);
      signOut().then(() => {
        window.location.href = import.meta.env.VITE_APP_URL || "/";
      });
    }
  }, [loading, profileLoading, user, isPlatformAdmin, signOut]);

  if (loading || profileLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-center">
          <ShieldX className="h-10 w-10 text-destructive" />
          <p className="text-lg font-semibold text-foreground">Not authorized</p>
          <p className="text-sm text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}
